/**
 * Profile service with server sync
 *
 * Wraps @yada/storage profile functions to add server synchronization.
 * Auth server is the source of truth, localStorage is cache.
 */

import type { Profile, ProfileType, Visibility } from '@yada/types';
import {
  deleteProfile as deleteProfileLocal,
  setActiveProfile as setActiveProfileLocal,
  setProfile,
  setActiveProfileId,
  getProfiles,
} from './accountService';
import {
  createProfile as createProfileServer,
  updateProfile as updateProfileServer,
  deleteProfile as deleteProfileServer,
  getAccessToken,
} from '@yada/api-client';

/**
 * Create a new profile (synced to server)
 */
export async function createProfile(data: {
  name: string;
  profileType: ProfileType;
  visibility?: Visibility;
  metadata?: Record<string, unknown>;
}): Promise<Profile> {
  console.log('[profileService] ═══════════════════════════════════════════════');
  console.log('[profileService] DIAGNOSTIC: Creating profile');
  console.log('[profileService]   Name:', data.name);
  console.log('[profileService]   Type:', data.profileType);

  // Get auth token
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated - cannot create profile');
  }

  // Create on server first (source of truth)
  const serverProfile = await createProfileServer(token, {
    name: data.name,
    profile_type: data.profileType,
    visibility: data.visibility,
    metadata: data.metadata,
  });

  console.log('[profileService]   Server assigned Profile ID:', serverProfile.id);
  console.log('[profileService]   Account ID:', serverProfile.account_id);

  // Convert server response to Profile type
  const profile: Profile = {
    id: serverProfile.id,
    accountId: serverProfile.account_id,
    profileType: serverProfile.profile_type as ProfileType,
    name: serverProfile.name,
    verificationLevel: serverProfile.verification_level,
    visibility: serverProfile.visibility as Visibility,
    metadata: serverProfile.metadata,
    createdAt: new Date(serverProfile.created_at),
    updatedAt: new Date(serverProfile.updated_at),
  };

  // Cache in localStorage (using the internal function directly to avoid double creation)
  await setProfile(profile);

  // If this is the first profile or an internal profile, set as active
  const profiles = await getProfiles();
  if (profiles.length === 1 || profile.profileType === 'internal') {
    console.log('[profileService]   Setting as active profile (first or internal)');
    await setActiveProfileId(profile.id);
  }

  console.log('[profileService] ═══════════════════════════════════════════════');

  // Notify all UI components (ProfileContext, etc.) that profiles have changed
  window.dispatchEvent(new CustomEvent('yada:profiles:updated'));

  return profile;
}

/**
 * Update an existing profile (synced to server)
 */
export async function updateProfile(
  id: string,
  updates: {
    name?: string;
    visibility?: Visibility;
    metadata?: Record<string, unknown>;
  }
): Promise<Profile> {
  // Get auth token
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated - cannot update profile');
  }

  // Update on server first (source of truth)
  const serverProfile = await updateProfileServer(token, id, updates);

  // Convert server response to Profile type
  const profile: Profile = {
    id: serverProfile.id,
    accountId: serverProfile.account_id,
    profileType: serverProfile.profile_type as ProfileType,
    name: serverProfile.name,
    verificationLevel: serverProfile.verification_level,
    visibility: serverProfile.visibility as Visibility,
    metadata: serverProfile.metadata,
    createdAt: new Date(serverProfile.created_at),
    updatedAt: new Date(serverProfile.updated_at),
  };

  // Update localStorage cache
  await setProfile(profile);

  // Notify all UI components (ProfileContext, etc.) that profiles have changed
  window.dispatchEvent(new CustomEvent('yada:profiles:updated'));

  return profile;
}

/**
 * Delete a profile (synced to server)
 */
export async function deleteProfile(id: string): Promise<void> {
  // Get auth token
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated - cannot delete profile');
  }

  // Delete from server first (source of truth)
  await deleteProfileServer(token, id);

  // Delete from localStorage cache
  await deleteProfileLocal(id);

  // Notify all UI components (ProfileContext, etc.) that profiles have changed
  window.dispatchEvent(new CustomEvent('yada:profiles:updated'));
}

/**
 * Set active profile (localStorage only, doesn't need server sync)
 */
export async function setActiveProfile(profileId: string): Promise<void> {
  await setActiveProfileLocal(profileId);
}

/**
 * Reorder profiles and notify UI
 */
export async function reorderProfiles(orderedIds: string[]): Promise<void> {
  const { reorderProfiles: reorderLocal } = await import('./accountService');
  await reorderLocal(orderedIds);
  window.dispatchEvent(new CustomEvent('yada:profiles:updated'));
}

/**
 * Export read-only functions directly from storage
 * These read from localStorage cache which is synced on login
 */
export {
  getCurrentProfile,
  getProfilesForCurrentAccount,
  getCurrentAccount,
  setProfiles,
} from './accountService';

